import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Fingerprint, Pulse, ShieldCheck, EnvelopeSimple, ClockCountdown, Warning, Gear, SignOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import GiantButton from "@/components/GiantButton";
import StatusDot from "@/components/StatusDot";
import StatusWidget from "@/components/StatusWidget";
import ContactsList from "@/components/ContactsList";
import ShareSection from "@/components/ShareSection";
import VacationControl from "@/components/VacationControl";
import PinDialog from "@/components/PinDialog";
import CheckinHistory from "@/components/CheckinHistory";
import ContactNote from "@/components/ContactNote";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatHMS = (totalSeconds) => {
  const s = Math.max(0, totalSeconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return [h, m, sec].map((n) => String(n).padStart(2, "0")).join(":");
};

const formatTimestamp = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const PulsePage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pressing, setPressing] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [pinOpen, setPinOpen] = useState(false);
  const [pinError, setPinError] = useState(null);
  const tickRef = useRef(null);

  const fetchState = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/state`, { withCredentials: true });
      setState(res.data);
      setSeconds(res.data.seconds_remaining);
    } catch (e) {
      if (e?.response?.status === 401) {
        navigate("/", { replace: true });
        return;
      }
      console.error("fetch state failed", e);
      toast.error("No se pudo conectar con el servidor");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchState();
    const sync = setInterval(fetchState, 30000);
    return () => clearInterval(sync);
  }, [fetchState]);

  // Local 1s tick
  useEffect(() => {
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      setSeconds((s) => (s > -86400 ? s - 1 : s));
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, []);

  const performCheckin = useCallback(
    async (pin = null) => {
      setPressing(true);
      setPinError(null);
      try {
        const res = await axios.post(
          `${API}/checkin`,
          pin ? { pin } : {},
          { withCredentials: true }
        );
        setSeconds(res.data.seconds_remaining);
        toast.success("Check-in confirmado", {
          description: `Temporizador reiniciado a ${state?.interval_hours ?? 48} horas.`,
        });
        setPinOpen(false);
        await fetchState();
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401 && state?.pin_required) {
          setPinError("PIN incorrecto. Intenta de nuevo.");
        } else if (status === 401) {
          navigate("/", { replace: true });
        } else {
          toast.error("Error al registrar check-in");
        }
      } finally {
        setTimeout(() => setPressing(false), 250);
      }
    },
    [fetchState, state, navigate]
  );

  const handleGiantClick = () => {
    if (state?.pin_required) {
      setPinError(null);
      setPinOpen(true);
    } else {
      performCheckin();
    }
  };

  const intervalSeconds = (state?.interval_hours ?? 48) * 3600;
  const fractionLeft = state ? Math.max(0, Math.min(1, seconds / intervalSeconds)) : 1;

  const paused = state?.paused === true;

  // Status: paused > expired > critical > warning > safe
  let statusKey = "safe";
  let statusLabel = "Estado: Seguro";
  if (paused) {
    statusKey = "paused";
    statusLabel = "Estado: En pausa";
  } else if (seconds <= 0) {
    statusKey = "expired";
    statusLabel = "Estado: Alerta enviada";
  } else if (seconds < 3600) {
    statusKey = "critical";
    statusLabel = "Estado: Crítico";
  } else if (seconds < 24 * 3600) {
    statusKey = "warning";
    statusLabel = "Estado: Atención";
  }

  return (
    <div className="min-h-screen w-full text-white" data-testid="pulse-page">
      {/* Header */}
      <header className="w-full border-b border-border/60">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Pulse size={26} weight="duotone" className="text-aqua" />
            <span
              className="font-heading font-black text-xl tracking-[0.4em]"
              data-testid="app-logo"
            >
              AQUÍSIGO
            </span>
          </div>
          <div className="flex items-center gap-3 sm:gap-5" data-testid="status-indicator">
            <div className="flex items-center gap-2">
              <StatusDot statusKey={statusKey} />
              <span className="text-xs uppercase tracking-[0.25em] text-white/70 hidden md:inline">
                {statusLabel}
              </span>
            </div>
            <div className="hidden sm:flex items-center gap-2 pl-3 border-l border-border">
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="w-7 h-7 rounded-full border border-border"
                />
              ) : (
                <div className="w-7 h-7 rounded-full bg-aqua/15 border border-aqua/30 text-aqua text-xs font-bold flex items-center justify-center">
                  {(user?.name || "U")[0].toUpperCase()}
                </div>
              )}
              <span
                className="text-sm text-white/80 truncate max-w-[140px] hidden lg:inline"
                data-testid="header-user-name"
              >
                {user?.name}
              </span>
            </div>
            <button
              onClick={() => navigate("/settings")}
              className="p-2 rounded-lg border border-border hover:border-aqua/50 hover:text-aqua text-white/70 transition-colors"
              title="Ajustes"
              data-testid="header-settings-btn"
            >
              <Gear size={18} weight="duotone" />
            </button>
            <button
              onClick={logout}
              className="p-2 rounded-lg border border-border hover:border-danger/50 hover:text-danger text-white/55 transition-colors"
              title="Cerrar sesión"
              data-testid="header-logout-btn"
            >
              <SignOut size={18} weight="duotone" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-12 py-10 md:py-14">
        {/* Headline */}
        <section className="mb-10 md:mb-14">
          <p className="text-xs uppercase tracking-[0.4em] text-white/50 mb-3">
            Dead-man's switch · Intervalo {state?.interval_hours ?? 48}h
          </p>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Presiona cada {state?.interval_hours ?? 48} horas.
            <br />
            <span className="text-aqua">Ellos sabrán que estás bien.</span>
          </h1>
          <p className="mt-4 max-w-2xl text-base text-white/70">
            Si no registras un check-in dentro del intervalo, AquíSigo enviará automáticamente
            un correo a tus contactos de emergencia avisándoles que algo te pudo haber pasado.
          </p>
        </section>

        {/* Vacation banner (when active) */}
        {paused && (
          <VacationControl
            paused={paused}
            vacationUntil={state?.vacation_until}
            onChanged={fetchState}
          />
        )}

        {/* Countdown */}
        <section className="mb-12 md:mb-16 text-center">
          <p className="text-xs uppercase tracking-[0.35em] text-white/50 mb-3">
            {paused ? "Temporizador en pausa" : "Tiempo restante"}
          </p>
          <div
            className={
              "mono font-mono tabular-nums text-5xl sm:text-7xl lg:text-8xl font-medium tracking-tight " +
              (paused
                ? "text-warn drop-shadow-[0_0_25px_rgba(255,153,0,0.4)]"
                : statusKey === "expired"
                ? "text-danger drop-shadow-[0_0_25px_rgba(255,42,85,0.5)]"
                : statusKey === "critical"
                ? "text-danger drop-shadow-[0_0_25px_rgba(255,42,85,0.4)]"
                : statusKey === "warning"
                ? "text-warn drop-shadow-[0_0_25px_rgba(255,153,0,0.4)]"
                : "text-aqua drop-shadow-[0_0_25px_rgba(0,229,255,0.45)]")
            }
            data-testid="countdown-timer-display"
          >
            {paused ? "—— : —— : ——" : seconds <= 0 ? "00:00:00" : formatHMS(seconds)}
          </div>
          {!paused && seconds <= 0 && (
            <div
              className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-danger/10 border border-danger/40 text-danger text-xs uppercase tracking-[0.25em]"
              data-testid="expired-badge"
            >
              <Warning size={14} weight="fill" /> Tiempo expirado · Alertas{" "}
              {state?.alert_sent ? "enviadas" : "pendientes"}
            </div>
          )}
        </section>

        {/* Giant Button */}
        <section className="mb-14 flex justify-center">
          <GiantButton
            onClick={handleGiantClick}
            pressing={pressing}
            disabled={loading || paused}
            statusKey={statusKey}
            data-testid="giant-check-in-button"
          />
        </section>

        {/* Helper text */}
        <p className="text-center text-sm text-white/55 mb-6 max-w-md mx-auto">
          {state?.pin_required
            ? "Toca el botón e introduce tu PIN para registrar tu check-in."
            : "Mantén presionado o toca el botón para registrar tu check-in. El temporizador se reinicia inmediatamente."}
        </p>

        {/* Vacation trigger (only when not paused) */}
        {!paused && (
          <div className="flex justify-center mb-12">
            <VacationControl
              paused={false}
              vacationUntil={null}
              onChanged={fetchState}
            />
          </div>
        )}

        {/* Status widgets */}
        <section
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-12"
          data-testid="status-widgets"
        >
          <StatusWidget
            icon={<ClockCountdown size={20} weight="duotone" />}
            label="Último check-in"
            value={formatTimestamp(state?.last_checkin)}
            testId="widget-last-checkin"
          />
          <StatusWidget
            icon={<ShieldCheck size={20} weight="duotone" />}
            label="Próximo deadline"
            value={formatTimestamp(state?.next_deadline)}
            testId="widget-deadline"
          />
          <StatusWidget
            icon={<EnvelopeSimple size={20} weight="duotone" />}
            label="Contactos"
            value={`${state?.contact_count ?? 0} configurados`}
            testId="widget-contacts"
          />
          <StatusWidget
            icon={<Fingerprint size={20} weight="duotone" />}
            label="Propietario"
            value={state?.owner_name ?? "—"}
            testId="widget-owner"
          />
        </section>

        {/* Progress bar */}
        <section className="mb-12">
          <div className="flex items-center justify-between text-xs uppercase tracking-[0.25em] text-white/50 mb-2">
            <span>Tiempo restante del ciclo</span>
            <span className="mono font-mono">{Math.round(fractionLeft * 100)}%</span>
          </div>
          <div className="h-1.5 w-full bg-surface-elevated rounded-full overflow-hidden border border-border">
            <motion.div
              className={
                "h-full " +
                (statusKey === "expired" || statusKey === "critical"
                  ? "bg-danger"
                  : statusKey === "warning"
                  ? "bg-warn"
                  : "bg-aqua")
              }
              initial={false}
              animate={{ width: `${fractionLeft * 100}%` }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            />
          </div>
        </section>

        {/* Contacts (read-only summary, edit via Settings) */}
        <section className="mb-10">
          <ContactsList contacts={state?.contacts ?? []} />
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-white/45">
            <span>Para añadir, editar o eliminar contactos</span>
            <button
              onClick={() => navigate("/settings")}
              className="text-aqua hover:underline inline-flex items-center gap-1"
              data-testid="contacts-edit-link"
            >
              <Gear size={12} weight="bold" />
              ve a Ajustes
            </button>
          </div>
        </section>

        {/* Check-in history */}
        <section className="mb-10">
          <CheckinHistory items={state?.checkin_history ?? []} />
        </section>

        {/* Share section */}
        <section className="mb-16">
          <ShareSection />
        </section>

        {/* Footer */}
        <ContactNote />
        <footer className="border-t border-border/60 pt-6 pb-2 text-xs text-white/40 flex flex-wrap items-center justify-between gap-2">
          <span>AquíSigo · Sistema de seguridad personal</span>
          <span className="mono">v1.0</span>
        </footer>
      </main>

      <PinDialog
        open={pinOpen}
        onOpenChange={(o) => {
          setPinOpen(o);
          if (!o) setPinError(null);
        }}
        pinLength={state?.pin_length ?? 4}
        submitting={pressing}
        errorMsg={pinError}
        onSubmit={(pin) => performCheckin(pin)}
      />
    </div>
  );
};

export default PulsePage;
